import { useMemo, useState } from "react";

import { ExportOrganisationsButton } from "components/organisations/ExportOrganisationsButton";
import Divider from "components/ui/divider/Divider";
import Modal from "components/ui/modal/Modal";
import { SearchOrganisationsViaOsQuery } from "lib/generated/app-service-gql/graphql";
import { useOrganisations } from "lib/hooks/api/organisations/useOrganisations";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import { PaywallProvider } from "lib/providers/Paywall";
import { ProHelperProvider } from "lib/providers/ProHelper";
import { SortState } from "lib/search/types";
import { ExportOrgCountField } from "lib/types/graphQLEnums";
import { BuyerColumns } from "./utils/getBuyersColumnCopy";
import { BuyerTable } from "./BuyerTable";
import { SaveBuyerButton } from "./SaveBuyerButton";
import { TableData } from "./types";
import {
  convertBuyerFiltersToOrgFilters,
  DEFAULT_BUYER_FILTERS,
  DEFAULT_BUYER_SORT,
} from "./useFilterableBuyerTableState";

import css from "../organisations/OrganisationsModal.module.scss";

const BUYER_MODAL_COLUMNS: BuyerColumns[] = ["name", "noticeCount", "buyerType"];

export type BuyersModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guids: string[];
};

/**
 * Lists the buyers on a notice so a subset can be picked before saving them to a list.
 * Mirrors SuppliersModal, so both organisation types behave the same on a notice preview.
 */
export function BuyersModal({ open, onOpenChange, guids }: BuyersModalProps) {
  const [selectedBuyers, setSelectedBuyers] = useState<string[]>(guids);
  const [sortOrder, setSortOrder] = useState<SortState>(DEFAULT_BUYER_SORT);
  const [pageSize, setPageSize] = useTablePageSize("buyers-modal-table");
  const [currentPage, setCurrentPage] = useState(1);

  const buyerGuids = selectedBuyers.length > 0 ? selectedBuyers : guids;

  const filters = useMemo(() => ({ ...DEFAULT_BUYER_FILTERS, buyerGuids: guids }), [guids]);
  const orgFilters = useMemo(
    () => convertBuyerFiltersToOrgFilters(filters, sortOrder, pageSize, currentPage),
    [filters, sortOrder, pageSize, currentPage],
  );

  const queryResult = useOrganisations(
    { ...orgFilters, include: { ...orgFilters.include, buyerLists: true } },
    { enabled: open },
  );

  const buyerData: TableData<SearchOrganisationsViaOsQuery | undefined> = {
    queryResult,
    totalCount: queryResult.data?.searchOrganisations.total,
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <PaywallProvider>
        <ProHelperProvider>
          <div className={css.modalContainer}>
            <h3 className={css.title}>Buyers on this notice</h3>
            <Divider className={css.divider} />
            <div className={css.modal}>
              <div className={css.modalHeader}>
                <h3>{buyerGuids.length} buyers</h3>

                <div className={css.actions}>
                  {selectedBuyers.length > 0 && (
                    <SaveBuyerButton
                      buyerGuids={buyerGuids}
                      trackingData={{ "Context source": "Notice preview" }}
                    />
                  )}
                  <ExportOrganisationsButton
                    filters={{ ...orgFilters, ids: buyerGuids }}
                    selectedOrganisationIds={buyerGuids}
                    countFields={[ExportOrgCountField.NoticeCount]}
                  />
                </div>
              </div>
              <div className={css.table}>
                <BuyerTable
                  filters={filters}
                  tableData={buyerData}
                  sortOrder={sortOrder}
                  onSortChange={(sort) => {
                    setSortOrder(sort);
                    setCurrentPage(1);
                  }}
                  selectedColumns={BUYER_MODAL_COLUMNS}
                  selectedRows={selectedBuyers}
                  onSelectedRowsChange={(rows) => setSelectedBuyers(rows)}
                  pagination={{ current: currentPage, pageSize }}
                  onPaginationChange={({ current, pageSize }) => {
                    setCurrentPage(current);
                    setPageSize(pageSize);
                  }}
                />
              </div>
            </div>
          </div>
        </ProHelperProvider>
      </PaywallProvider>
    </Modal>
  );
}
